/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { detectBrowser } from '../helpers/generalHelper'

/**
 * General Helper Service
 * Wraps helper utilities for use in service context
 */
export class GeneralHelperService {
  /**
   * Detect the current browser type and platform
   * @returns Object with browser and platform detection flags
   */
  detectBrowser() {
    return detectBrowser()
  }

  /**
   * Get URL parameter by name
   * @param name - Parameter name
   * @param url - URL to parse (defaults to current location)
   */
  getUrlParam(name: string, url?: string): string | null {
    const results = new RegExp('[?&]' + name + '=([^&#]*)').exec(url || window.location.search)
    if (results === null) {
      return null
    }
    return decodeURI(results[1]) || '0'
  }

  /**
   * Get cookie value by name
   * @param cookieName - Name of the cookie
   */
  getCookie(cookieName: string): string {
    const name = cookieName + '='
    const decodedCookie = decodeURIComponent(document.cookie)
    const ca = decodedCookie.split(';')
    for (let c of ca) {
      c = c.trim()
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length)
      }
    }
    return ''
  }

  /**
   * Capitalize first letter of string
   * @param str - String to capitalize
   */
  capitalizeString(str: string): string {
    return str ? str[0].toUpperCase() + str.slice(1).toLowerCase() : str
  }

  /**
   * Get number of decimal places in a number
   * @param num - Number to check
   */
  getNumberOfDecimals(num: number): number {
    const match = ('' + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/)
    if (!match) return 0
    return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0))
  }

  /**
   * Get size of object (number of keys)
   * @param obj - Object to measure
   */
  getSizeOfObject(obj: Record<string, any>): number {
    return Object.keys(obj).length
  }

  /**
   * Get domain without subdomain
   * @param url - URL to parse (defaults to current origin)
   */
  getDomainWithoutSubdomain(url?: string): string {
    const urlParts = (url ?? window.location.origin).split('.')
    return urlParts.slice(-(urlParts.length === 4 ? 3 : 2)).join('.')
  }

  /**
   * Get modal window template URL
   * @param type - Modal type ('draggable' or 'default')
   */
  getModalWindowTemplateUrl(type: 'draggable' | 'default' = 'default'): string {
    const PATH_TO_TEMPLATES = 'app/template/modal/'
    switch (type) {
      case 'draggable':
        return PATH_TO_TEMPLATES + 'draggable.modal.window.template.html'
      default:
        return PATH_TO_TEMPLATES + 'default.modal.window.template.html'
    }
  }

  /**
   * Dispatch mouse click event at coordinates
   * @param x - X coordinate
   * @param y - Y coordinate
   */
  dispatchMouseEvent(x: number, y: number): void {
    const ev = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
      screenX: x,
      screenY: y
    })
    const el = document.elementFromPoint(x, y)
    el?.dispatchEvent(ev)
  }
}

// Export singleton instance
export const generalHelper = new GeneralHelperService()

// Export individual functions for convenience
export {
  detectBrowser,
  getCookie,
  capitalizeString,
  getNumberOfDecimals,
  getModalWindowTemplateUrl,
  getSizeOfObject,
  getDomainWithoutSubdomain,
  getUrlParam,
  dispatchMouseEvent
} from '../helpers/generalHelper'
